// ==============================================
// Dashboard Page
// ==============================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { bookingsAPI, matchesAPI } from '../services/api';
import { Booking, Match } from '../types';
import { clubPath, playerPath } from '../utils/slug';

const QUICK_ACTIONS = [
  { icon: '📅', title: 'Rezervari mese', desc: 'Rezerva o masa la clubul tau favorit', to: '/bookings', color: 'si-blue'   },
  { icon: '🏆', title: 'Turnee',    desc: 'Inscrie-te la turnee viitoare',           to: '/tournaments', color: 'si-orange' },
  { icon: '🏓', title: 'Gaseste cluburi',     desc: 'Descopera cluburi in apropiere',             to: '/clubs',       color: 'si-green'  },
  { icon: '👨‍🏫', title: 'Gaseste antrenori', desc: 'Rezerva sedinte de coaching',       to: '/trainers',    color: 'si-purple' },
  { icon: '📊', title: 'Clasamente',       desc: 'Vezi clasamentele jucatorilor',                to: '/rankings',    color: 'si-cyan'   },
  { icon: '🎯', title: 'Hub meciuri',    desc: 'Provocari deschise si trimite rezultate',  to: '/matches',     color: 'si-purple' },
  { icon: '👥', title: 'Gaseste jucatori',   desc: 'Conecteaza-te cu alti jucatori',          to: '/players',     color: 'si-red'    },
];

// ── helpers ──────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, '0');
function localIso(d: Date = new Date()) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
const TODAY = localIso();

function daysUntil(iso: string): number {
  const [y, mo, d] = iso.split('-').map(Number);
  const target = new Date(y, mo - 1, d).getTime();
  const now    = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime();
  return Math.round((target - now) / 86400000);
}

function fmtBookingDate(iso: string): string {
  const [y, mo, day] = iso.split('-').map(Number);
  return new Date(y, mo - 1, day).toLocaleDateString('ro-RO', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}

function fmtShortDate(iso: string): string {
  const [y, mo, day] = iso.split('-').map(Number);
  return new Date(y, mo - 1, day).toLocaleDateString('ro-RO', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function daysSince(iso?: string | null): number | null {
  if (!iso) return null;
  const [y, mo, d] = iso.split('-').map(Number);
  const target = new Date(y, mo - 1, d).getTime();
  if (Number.isNaN(target)) return null;
  const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime();
  return Math.max(0, Math.round((today - target) / 86400000));
}

function fmtLastMatchAgo(iso?: string | null): string {
  const days = daysSince(iso);
  if (days === null) return 'Niciun meci';
  if (days === 0) return 'Astăzi';
  if (days === 1) return 'Ieri';
  if (days < 31) return `acum ${days} zile`;

  const months = Math.floor(days / 30);
  if (months < 12) return `acum ${months} ${months === 1 ? 'lună' : 'luni'}`;

  const years = Math.floor(months / 12);
  return `acum ${years} ${years === 1 ? 'an' : 'ani'}`;
}

function matchOpponent(m: Match, userId?: number | null): string {
  const isP1 = m.player1_id === userId;
  return isP1
    ? `${m.player2_first_name ?? ''} ${m.player2_last_name ?? ''}`.trim()
    : `${m.player1_first_name ?? ''} ${m.player1_last_name ?? ''}`.trim();
}

function matchOpponentProfile(m: Match, userId?: number | null) {
  const isP1 = m.player1_id === userId;
  return isP1
    ? {
      id: m.player2_id,
      first_name: m.player2_first_name || '',
      last_name: m.player2_last_name || '',
      avatar_url: m.player2_avatar_url || null,
      level_name: m.player2_level_name || null,
      level_slug: m.player2_level_slug || null,
    }
    : {
      id: m.player1_id,
      first_name: m.player1_first_name || '',
      last_name: m.player1_last_name || '',
      avatar_url: m.player1_avatar_url || null,
      level_name: m.player1_level_name || null,
      level_slug: m.player1_level_slug || null,
    };
}

function getLevelBadgeClass(slug?: string | null) {
  switch (slug) {
    case 'elite':
    case 'profesionisti': return 'cat-elite';
    case 'open': return 'cat-a';
    case 'avansati': return 'cat-b';
    case 'hobby': return 'cat-c';
    case 'amator': return 'cat-d';
    default: return 'cat-d';
  }
}

function matchResult(m: Match, userId?: number | null): 'W' | 'L' | 'T' {
  if (m.result === 'tie') return 'T';
  return m.winner_id === userId ? 'W' : 'L';
}

function matchTypeLabel(slug?: string | null) {
  if (slug === 'competitive') return 'Competitiv';
  if (slug === 'tournament') return 'Turneu';
  return 'Amical';
}

function calcStreak(results: Array<'W' | 'L' | 'T'>) {
  const first = results.find(r => r !== 'T');
  if (!first) return { type: 'T' as const, count: results.length };
  let count = 0;
  for (const r of results) {
    if (r !== first) break;
    count++;
  }
  return { type: first, count };
}

// ── component ─────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const siteUrl = process.env.REACT_APP_SITE_URL || '';
  const avatarSrc = user?.avatar_url ? `${siteUrl}${user.avatar_url}` : null;
  const initials = user ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase() : '';

  const [nextBooking, setNextBooking]   = useState<Booking | null>(null);
  const [loadingBooking, setLoadingBooking] = useState(true);
  const [matches, setMatches] = useState<Match[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(true);

  useEffect(() => {
    bookingsAPI.getAll({ my_bookings: true })
      .then(res => {
        if (res.success && res.data) {
          const upcoming = (res.data.bookings || [])
            .filter(b => b.booking_date >= TODAY && b.status !== 'cancelled')
            .sort((a, b) => a.booking_date.localeCompare(b.booking_date) || a.start_time.localeCompare(b.start_time));
          setNextBooking(upcoming[0] ?? null);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingBooking(false));
  }, []);

  useEffect(() => {
    matchesAPI.getAll({ mine: true })
      .then(res => {
        if (res.success && res.data) setMatches(res.data.matches || []);
      })
      .catch(() => {})
      .finally(() => setLoadingMatches(false));
  }, []);

  const completedMatches = matches.filter(m => m.status === 'completed');
  const recentMatches = completedMatches.slice(0, 5);
  const lastMatch = completedMatches[0] ?? null;
  const lastMatchAgo = fmtLastMatchAgo(lastMatch?.match_date);
  const recentResults = recentMatches.map(m => matchResult(m, user?.id));
  const wins = completedMatches.filter(m => matchResult(m, user?.id) === 'W').length;
  const losses = completedMatches.filter(m => matchResult(m, user?.id) === 'L').length;
  const ties = completedMatches.filter(m => matchResult(m, user?.id) === 'T').length;
  const winRate = completedMatches.length > 0 ? Math.round(wins / completedMatches.length * 100) : 0;
  const currentStreak = calcStreak(recentResults);
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  const matchesLastYear = completedMatches.filter(m => new Date(`${m.match_date}T00:00:00`) >= oneYearAgo).length;
  const avgMatchesPerWeek = Math.round((matchesLastYear / 52) * 10) / 10;
  const biggestWin = completedMatches
    .filter(m => matchResult(m, user?.id) === 'W')
    .sort((a, b) => (b.player1_score - b.player2_score) - (a.player1_score - a.player2_score))[0] ?? null;

  const getFormColor = (result: string) => {
    if (result === 'W') return '#10b981';
    if (result === 'T') return '#64748b';
    return '#ef4444';
  };

  const memberSince = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('ro-RO', { year: 'numeric', month: 'long' })
    : '—';

  return (
    <div className="page">
      <div className="container page-inner">
        {/* Welcome banner */}
        <div className="profile-banner mb-4 fade-up hero-banner dashboard-hero">
          <div className="shade"></div>
          <div className="dashboard-hero-user d-flex align-items-center gap-3 mb-3">
            <div className="avatar avatar-lg" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: avatarSrc ? 'transparent' : 'rgba(255,255,255,.2)',
              color: '#fff', fontWeight: 700, fontSize: '1.25rem', overflow: 'hidden',
            }}>
              {avatarSrc
                ? <img src={avatarSrc} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : initials
              }
            </div>
            <div>
              <div className="fw-bold fs-5">Bine ai revenit, {user?.first_name}!</div>
              <div style={{ color: 'rgba(255,255,255,.6)', fontSize: '.875rem' }}>{user?.email}</div>
            </div>
          </div>

          <div className="dashboard-hero-stats row g-3">
            {[
              { label: 'Nivel',      value: user?.level_name ?? '—' },
              { label: 'Membru din', value: memberSince },
              { label: 'Ultimul meci', value: loadingMatches ? '—' : lastMatchAgo },
            ].map(s => (
              <div key={s.label} className="col-6 col-md-3">
                <div style={{ background: 'rgba(255,255,255,.29)', borderRadius: 10, padding: '12px 14px' }}>
                  <div className="stat-label" style={{ color: 'rgba(255,255,255,.5)' }}>{s.label}</div>
                  <div className="stat-value" style={{ color: '#fff', fontSize: '1.1rem' }}>{s.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Next booking ── */}
        <div className="dashboard-section mb-4">
          <div className="dashboard-section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.875rem' }}>
            <h5 style={{ fontWeight: 800, margin: 0 }}>Urmātoarea rezervare</h5>
            <Link to="/bookings" style={{ fontSize: '.82rem', color: 'var(--tt-primary)', textDecoration: 'none', fontWeight: 600 }}>
              Toate rezervările →
            </Link>
          </div>

          {loadingBooking ? (
            <div style={{
              background: 'var(--tt-surface)', border: '1px solid var(--tt-border)',
              borderRadius: 16, padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem',
            }}>
              <span className="spinner-border spinner-border-sm" style={{ color: 'var(--tt-primary)' }} />
              <span style={{ color: 'var(--tt-text-muted)', fontSize: '.875rem' }}>Se încarcă…</span>
            </div>

          ) : nextBooking ? (() => {
            const days = daysUntil(nextBooking.booking_date);
            const isToday    = days === 0;
            const isTomorrow = days === 1;
            const tagLabel   = isToday ? 'Astăzi' : isTomorrow ? 'Mâine' : `în ${days} zile`;
            const tagColor   = isToday ? '#15803d' : isTomorrow ? '#92400e' : 'var(--tt-primary)';
            const tagBg      = isToday ? '#f0fdf4' : isTomorrow ? '#fffbeb' : '#eff6ff';
            const tagBorder  = isToday ? '#bbf7d0' : isTomorrow ? '#fde68a' : '#bfdbfe';
            const clubName   = nextBooking.club_name || '—';
            const dur        = (() => {
              const [sh, sm] = nextBooking.start_time.split(':').map(Number);
              const [eh, em] = nextBooking.end_time.split(':').map(Number);
              const mins = (eh * 60 + em) - (sh * 60 + sm);
              const h = Math.floor(mins / 60), m = mins % 60;
              return m === 0 ? `${h}h` : `${h}h${m}`;
            })();

            return (
              <div className="dashboard-booking-card" style={{
                background: 'var(--tt-surface)', border: '1px solid var(--tt-border)',
                borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--sh-sm)',
                display: 'flex',
              }}>
                {/* Accent sidebar */}
                <div style={{
                  width: 5, flexShrink: 0,
                  background: isToday ? '#16a34a' : isTomorrow ? '#f59e0b' : 'var(--tt-primary)',
                }} />

                <div className="dashboard-booking-body" style={{ flex: 1, padding: '1.25rem 1.5rem' }}>
                  {/* Top row */}
                  <div className="dashboard-booking-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
                    <div className="dashboard-booking-main" style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
                      {/* Date badge */}
                      <div className="dashboard-date-badge" style={{
                        textAlign: 'center', background: 'var(--tt-surface-2)',
                        border: '1px solid var(--tt-border)', borderRadius: 12,
                        padding: '.5rem .875rem', flexShrink: 0,
                      }}>
                        <div style={{ fontSize: '.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--tt-text-muted)' }}>
                          {new Date(nextBooking.booking_date + 'T00:00:00').toLocaleDateString('ro-RO', { month: 'short' })}
                        </div>
                        <div style={{ fontSize: '1.6rem', fontWeight: 800, lineHeight: 1, color: 'var(--tt-text)' }}>
                          {new Date(nextBooking.booking_date + 'T00:00:00').getDate()}
                        </div>
                        <div style={{ fontSize: '.65rem', color: 'var(--tt-text-muted)', marginTop: 1 }}>
                          {new Date(nextBooking.booking_date + 'T00:00:00').toLocaleDateString('ro-RO', { weekday: 'short' })}
                        </div>
                      </div>

                      {/* Club + table */}
                      <div className="dashboard-booking-info">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', flexWrap: 'wrap' }}>
                          <Link
                            to={clubPath({ id: nextBooking.club_id, name: clubName })}
                            style={{ fontWeight: 800, fontSize: '1.05rem', color: 'var(--tt-text)', textDecoration: 'none' }}
                          >
                            {clubName}
                          </Link>
                          <span style={{
                            background: tagBg, color: tagColor, border: `1px solid ${tagBorder}`,
                            borderRadius: 999, padding: '.1rem .6rem',
                            fontSize: '.72rem', fontWeight: 700,
                          }}>{tagLabel}</span>
                        </div>
                        <div style={{ color: 'var(--tt-text-muted)', fontSize: '.83rem', marginTop: '.2rem' }}>
                          🏓 Masa {nextBooking.table_number}
                          {nextBooking.table_type ? ` · ${nextBooking.table_type}` : ''}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Time row */}
                  <div className="dashboard-time-pill" style={{
                    display: 'flex', alignItems: 'center', gap: '.5rem',
                    background: 'var(--tt-surface-2)', border: '1px solid var(--tt-border)',
                    borderRadius: 10, padding: '.6rem 1rem', marginBottom: '1rem',
                    width: 'fit-content',
                  }}>
                    <span style={{ fontSize: '1rem' }}>🕐</span>
                    <span style={{ fontWeight: 700, fontSize: '.92rem' }}>
                      {nextBooking.start_time.substring(0, 5)} – {nextBooking.end_time.substring(0, 5)}
                    </span>
                    <span style={{
                      background: 'var(--tt-border)', borderRadius: 999,
                      padding: '.1rem .5rem', fontSize: '.72rem', fontWeight: 600, color: 'var(--tt-text-muted)',
                    }}>{dur}</span>
                  </div>

                  {/* Full date text */}
                  <div style={{ fontSize: '.8rem', color: 'var(--tt-text-muted)', marginBottom: '1.1rem' }}>
                    {fmtBookingDate(nextBooking.booking_date)}
                  </div>

                  {/* CTAs */}
                  <div className="dashboard-booking-actions" style={{ display: 'flex', gap: '.625rem', flexWrap: 'wrap' }}>
                    <Link
                      to={clubPath({ id: nextBooking.club_id, name: clubName })}
                      className="btn btn-primary btn-sm"
                      style={{ borderRadius: 10, fontWeight: 700 }}
                    >
                      Mergi la club →
                    </Link>
                    <Link
                      to="/bookings"
                      className="btn btn-outline-secondary btn-sm"
                      style={{ borderRadius: 10 }}
                    >
                      Toate rezervările
                    </Link>
                  </div>
                </div>
              </div>
            );
          })() : (
            /* Empty state */
            <div style={{
              background: 'var(--tt-surface)', border: '2px dashed var(--tt-border)',
              borderRadius: 16, padding: '2rem 1.5rem',
              display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap',
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: 18, flexShrink: 0,
                background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.75rem',
              }}>🏓</div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: '.3rem' }}>
                  Nicio rezervare viitoare
                </div>
                <div style={{ color: 'var(--tt-text-muted)', fontSize: '.875rem', lineHeight: 1.5 }}>
                  Rezervă o masă la clubul tău favorit și pregătește-te de joc!
                </div>
              </div>
              <Link
                to="/clubs"
                className="btn btn-primary"
                style={{ borderRadius: 12, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Rezervă o masă →
              </Link>
            </div>
          )}
        </div>

        {/* Performance Stats Section */}
        <div className="page-header dashboard-performance-head mb-3">
          <h2>Statistici Performanta</h2>
          <p>Urmareste-ti progresul si performanta recenta</p>
        </div>

        <div className="row g-4 mb-4">
          {/* Recent Form Card */}
          <div className="col-lg-8">
            <div className="entity-card">
              <div className="entity-card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-1">Forma Recenta</h5>
                    <p className="text-muted mb-0" style={{ fontSize: '.875rem' }}>Ultimele 5 meciuri</p>
                  </div>
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    {(loadingMatches ? ['?', '?', '?', '?', '?'] : recentResults).map((result, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          background: loadingMatches ? 'rgba(148,163,184,.22)' : getFormColor(result),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: '700',
                          fontSize: '1rem',
                          boxShadow: '0 2px 8px rgba(0,0,0,.1)',
                          filter: loadingMatches ? 'blur(1px)' : undefined,
                        }}
                      >
                        {loadingMatches ? '' : result}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Matches List */}
                {loadingMatches ? (
                  <div className="d-grid gap-2">
                    {[0, 1, 2].map(i => (
                      <div key={i} style={{
                        height: 64, borderRadius: 10,
                        background: 'linear-gradient(90deg, rgba(148,163,184,.12), rgba(148,163,184,.22), rgba(148,163,184,.12))',
                        filter: 'blur(.4px)', opacity: .85,
                      }} />
                    ))}
                  </div>
                ) : recentMatches.length === 0 ? (
                  <div style={{
                    border: '1px dashed var(--tt-border)', borderRadius: 14,
                    padding: '2rem 1.25rem', textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(239,246,255,.75), rgba(255,255,255,.9))',
                  }}>
                    <div style={{
                      width: 58, height: 58, borderRadius: 16, margin: '0 auto .85rem',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(33,37,123,.08)', color: 'var(--tt-primary)',
                      fontSize: '1.65rem', filter: 'blur(.15px)',
                    }}>🏓</div>
                    <div style={{ fontWeight: 800, marginBottom: '.35rem' }}>Niciun meci adăugat încă</div>
                    <div style={{ color: 'var(--tt-text-muted)', fontSize: '.875rem', marginBottom: '1rem' }}>
                      După ce adaugi primul scor, aici vei vedea forma recentă și statisticile tale reale.
                    </div>
                    <Link to="/matches" className="btn btn-primary btn-sm" style={{ borderRadius: 10, fontWeight: 700 }}>
                      Adaugă primul meci
                    </Link>
                  </div>
                ) : (
                  <div className="d-grid gap-2">
                  {recentMatches.map((match, idx) => {
                    const result = matchResult(match, user?.id);
                    const opponent = matchOpponentProfile(match, user?.id);
                    const opponentName = `${opponent.first_name} ${opponent.last_name}`.trim() || 'Adversar';
                    const opponentAvatar = opponent.avatar_url ? `${siteUrl}${opponent.avatar_url}` : null;
                    const opponentInitials = `${opponent.first_name?.[0] ?? ''}${opponent.last_name?.[0] ?? ''}`.toUpperCase();
                    return (
                    <div
                      key={match.id}
                      className="dashboard-recent-match"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '.75rem',
                        background: idx % 2 === 0 ? '#f8fafc' : '#fff',
                        borderRadius: '8px',
                        gap: '1rem',
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '6px',
                          background: getFormColor(result),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          fontWeight: '700',
                          fontSize: '.85rem',
                          flexShrink: 0,
                        }}
                      >
                        {result}
                      </div>
                      <Link
                        to={playerPath({ id: opponent.id, first_name: opponent.first_name, last_name: opponent.last_name })}
                        className="dashboard-opponent-avatar"
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          flexShrink: 0,
                          background: opponentAvatar ? 'transparent' : 'linear-gradient(135deg, var(--tt-primary), var(--tt-accent))',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 900,
                          textDecoration: 'none',
                          boxShadow: '0 2px 8px rgba(15,23,42,.12)',
                        }}
                      >
                        {opponentAvatar
                          ? <img src={opponentAvatar} alt={opponentName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : opponentInitials
                        }
                      </Link>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <Link
                          to={playerPath({ id: opponent.id, first_name: opponent.first_name, last_name: opponent.last_name })}
                          className="fw-semibold"
                          style={{
                            fontSize: '.92rem',
                            color: 'var(--tt-text)',
                            textDecoration: 'none',
                            display: 'block',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          vs {opponentName}
                        </Link>
                        <div className="dashboard-match-meta" style={{ display: 'flex', alignItems: 'center', gap: '.35rem', flexWrap: 'wrap', marginTop: '.18rem' }}>
                          {opponent.level_name && <span className={`badge ${getLevelBadgeClass(opponent.level_slug)}`}>{opponent.level_name}</span>}
                          <span className="text-muted" style={{ fontSize: '.78rem' }}>
                            {match.tournament_title || matchTypeLabel(match.match_type_slug || match.match_type)}
                          </span>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold" style={{ fontSize: '.95rem' }}>{match.player1_score}-{match.player2_score}</div>
                        <div className="text-muted" style={{ fontSize: '.75rem' }}>{fmtShortDate(match.match_date)}</div>
                      </div>
                    </div>
                    );
                  })}
                  </div>
                )}

                <Link to="/matches" className="btn btn-outline-primary btn-sm w-100 mt-3">
                  Vezi Toate Meciurile
                </Link>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="col-lg-4">
            <div className="dashboard-stat-grid row g-2 g-lg-3">
              {/* Win Rate */}
              <div className="col-6 col-lg-12"><div className="entity-card h-100">
                <div className="entity-card-body">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                      }}
                    >
                      🏆
                    </div>
                    <div>
                      <div className="text-muted" style={{ fontSize: '.8rem' }}>Rata Victorii</div>
                      <div className="fw-bold" style={{ fontSize: '1.75rem', lineHeight: 1 }}>{loadingMatches ? '—' : `${winRate}%`}</div>
                    </div>
                  </div>
                  <div style={{ background: '#e5e7eb', borderRadius: '999px', height: '8px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${loadingMatches ? 0 : winRate}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #10b981, #059669)',
                        borderRadius: '999px',
                      }}
                    />
                  </div>
                  <div className="text-muted mt-2" style={{ fontSize: '.75rem' }}>
                    {loadingMatches ? 'Se încarcă…' : `${completedMatches.length} meciuri · ${wins} V / ${losses} I / ${ties} E`}
                  </div>
                </div>
              </div></div>

              {/* Biggest Win */}
              <div className="col-6 col-lg-12"><div className="entity-card h-100">
                <div className="entity-card-body">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #EF8628, #d67320)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                      }}
                    >
                      ⭐
                    </div>
                    <div>
                      <div className="text-muted" style={{ fontSize: '.8rem' }}>Cea Mai Mare Victorie</div>
                      <div className="fw-bold" style={{ fontSize: '1.5rem', lineHeight: 1 }}>
                        {biggestWin ? `${biggestWin.player1_score}-${biggestWin.player2_score}` : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="text-muted" style={{ fontSize: '.85rem' }}>
                    <strong>{biggestWin ? `vs ${matchOpponent(biggestWin, user?.id)}` : 'Adaugă un rezultat câștigat'}</strong>
                  </div>
                  <div className="text-muted" style={{ fontSize: '.75rem' }}>{biggestWin ? fmtShortDate(biggestWin.match_date) : 'Nu există încă'}</div>
                </div>
              </div></div>

              {/* Last Match */}
              <div className="col-6 col-lg-12"><div className="entity-card h-100">
                <div className="entity-card-body">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                      }}
                    >
                      🗓️
                    </div>
                    <div>
                      <div className="text-muted" style={{ fontSize: '.8rem' }}>Ultimul Meci</div>
                      <div className="fw-bold" style={{ fontSize: '1.35rem', lineHeight: 1.1 }}>
                        {loadingMatches ? '—' : lastMatchAgo}
                      </div>
                    </div>
                  </div>
                  <div className="text-muted" style={{ fontSize: '.75rem' }}>
                    {lastMatch ? `${fmtShortDate(lastMatch.match_date)} · vs ${matchOpponent(lastMatch, user?.id) || 'Adversar'}` : 'Nu există încă meciuri confirmate'}
                  </div>
                </div>
              </div></div>

              {/* Match Activity */}
              <div className="col-6 col-lg-12"><div className="entity-card h-100">
                <div className="entity-card-body">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #21257B, #1a1e62)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                      }}
                    >
                      📊
                    </div>
                    <div>
                      <div className="text-muted" style={{ fontSize: '.8rem' }}>Medie Pe Saptamana</div>
                      <div className="fw-bold" style={{ fontSize: '1.75rem', lineHeight: 1 }}>{loadingMatches ? '—' : avgMatchesPerWeek}</div>
                    </div>
                  </div>
                  <div className="text-muted" style={{ fontSize: '.75rem' }}>
                    Meciuri/saptamana (ultimul an)
                  </div>
                </div>
              </div></div>

              {/* Current Streak */}
              <div className="col-6 col-lg-12"><div className="entity-card h-100">
                <div className="entity-card-body">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        background: currentStreak.type === 'W' 
                          ? 'linear-gradient(135deg, #10b981, #059669)'
                          : currentStreak.type === 'L'
                            ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                            : 'linear-gradient(135deg, #64748b, #475569)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                      }}
                    >
                      🔥
                    </div>
                    <div>
                      <div className="text-muted" style={{ fontSize: '.8rem' }}>Serie Curenta</div>
                      <div className="fw-bold" style={{ fontSize: '1.75rem', lineHeight: 1 }}>
                        {loadingMatches || recentResults.length === 0 ? '—' : `${currentStreak.count} ${currentStreak.type === 'W' ? 'V' : currentStreak.type === 'L' ? 'I' : 'E'}`}
                      </div>
                    </div>
                  </div>
                  <div className="text-muted" style={{ fontSize: '.75rem' }}>
                    {currentStreak.type === 'W' ? 'Victorii consecutive' : currentStreak.type === 'L' ? 'Înfrângeri consecutive' : 'Rezultate de egalitate'}
                  </div>
                </div>
              </div></div>
            </div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="page-header">
          <h2>Actiuni rapide</h2>
        </div>
        <div className="row g-3 mb-4">
          {QUICK_ACTIONS.map(a => (
            <div key={a.to} className="col-md-4 col-6">
              <Link to={a.to} className="quick-card">
                <div className={`stat-icon ${a.color} quick-card-icon`}>{a.icon}</div>
                <div className="quick-card-title">{a.title}</div>
                <p className="quick-card-desc">{a.desc}</p>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
